import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export function useAdminDashboard() {
  const [accessRequests, setAccessRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showAddAdvisorModal, setShowAddAdvisorModal] = useState(false);
  const [activeTab, setActiveTab] = useState('requests');
  const [usersRefreshKey, setUsersRefreshKey] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const fetchAdminInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/me`, { headers: authHeaders() });
      if (!response.ok) throw new Error('Not authenticated');
      const data = await response.json();
      setAdmin(data);
      if (data.role === 'infosec') setActiveTab('audit-log');
    } catch (err) {
      logger.error(err);
      navigate('/admin/login');
    }
  };

  const fetchAccessRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/access-requests`, { headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to fetch requests');
      let data = await response.json();
      const filterMap = {
        pending: r => r.status === 'pending', approved: r => r.status === 'approved',
        rejected: r => r.status === 'rejected', public: r => r.introducer_type === 'public',
        user_intro: r => r.introducer_type === 'user',
        advisor_intro: r => r.introducer_type === 'advisor' || r.introducer_type === 'trusted_professional',
      };
      if (filterMap[filter]) data = data.filter(filterMap[filter]);
      setAccessRequests(data);
    } catch (error) {
      logger.error('Error fetching access requests:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchUsers = useCallback(() => {
    // Triggers AdminUsersTab to reload from server using its current filters/pagination.
    setUsersRefreshKey(k => k + 1);
  }, []);

  const handleApprove = async (requestId) => {
    setActionLoading(requestId);
    try {
      const response = await fetch(`${API_URL}/api/admin/access-requests/${requestId}/approve`, { method: 'POST', headers: authHeaders() });
      if (!response.ok) throw new Error((await response.json()).detail || 'Failed to approve');
      toast({ title: "Request Approved", description: "User has been approved and will receive registration instructions.", className: "bg-green-600 text-white border-green-700" });
      await fetchAccessRequests();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleWaitlist = async (requestId) => {
    setActionLoading(requestId);
    try {
      const response = await fetch(`${API_URL}/api/admin/access-requests/${requestId}/reject`, { method: 'POST', headers: authHeaders() });
      if (!response.ok) throw new Error((await response.json()).detail || 'Failed to waitlist');
      toast({ title: "Request Waitlisted", description: "User has been moved to the waitlist.", className: "bg-orange-600 text-white border-orange-700" });
      await fetchAccessRequests();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Are you sure you want to soft delete user "${email}"?\n\nThis will mark the user as deleted (data preserved for audit), delete all access requests, registration tokens, and investments.\n\nThe user will be able to create a fresh access request.`)) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(email)}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error((await response.json()).detail || 'Failed to delete user');
      const result = await response.json();
      toast({ title: "User Deleted", description: `User "${email}" and ${result.deleted_records.access_requests + result.deleted_records.registration_tokens + result.deleted_records.investments} associated records soft deleted.`, className: "bg-green-600 text-white border-green-700" });
      fetchUsers();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, { method: 'POST', headers: authHeaders() });
    } catch (err) { logger.error('Logout cleanup failed:', err); }
    localStorage.removeItem('auth_token');
    navigate('/admin/login');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAdminInfo(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAccessRequests(); }, [filter]);

  const isRoleAllowed = (roles) => !roles || roles.includes(admin?.role);

  const visibleTabs = useMemo(() => {
    const TAB_CONFIG = [
      { key: 'requests', label: 'Invitation Requests', roles: null },
      { key: 'briefings', label: 'Briefings', roles: null },
      { key: 'estate-vault', label: 'Continuity System', roles: ['owner', 'admin'] },
      { key: 'users', label: 'Users Management', roles: null },
      { key: 'payments', label: 'Payments & Revenue', roles: null },
      { key: 'pricing', label: 'Pricing Management', roles: null },
      { key: 'security', label: 'Security', roles: null },
      { key: 'settings', label: 'Settings', roles: null },
      { key: 'portfolios', label: 'Portfolios', roles: null },
      { key: 'portfolio-access', label: 'Portfolio Access', roles: null },
      { key: 'identity-map', label: 'Identity Mapping', roles: ['owner'] },
      { key: 'roles', label: 'Roles', roles: null },
    ];
    return TAB_CONFIG.filter(t => isRoleAllowed(t.roles));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.role]);

  const visibleRoleTabs = useMemo(() => {
    const ROLE_TABS = [
      { key: 'audit-log', label: 'Audit Log', roles: ['owner', 'infosec'] },
      { key: 'feedback', label: 'Feedback', roles: ['owner', 'admin', 'engineer', 'product_manager'] },
      { key: 'advisors', label: 'Trusted Professionals', roles: ['owner', 'admin'] },
      { key: 'assisted-onboarding', label: 'Assisted Onboarding', roles: ['owner', 'admin', 'support'] },
      { key: 'walkthrough-videos', label: 'Walkthrough Videos', roles: ['owner', 'admin'] },
      { key: 'beta-program', label: 'Beta Program', roles: ['owner', 'admin'] },
      { key: 'campaigns', label: 'Campaigns', roles: ['owner', 'admin', 'support'] },
      { key: 'health-check', label: 'Health Check', roles: ['owner', 'admin'] },
      { key: 'code-health', label: 'Code Health', roles: ['owner', 'admin'] },
      { key: 'backups', label: 'Backups', roles: ['owner', 'admin', 'engineer'] },
    ];
    return ROLE_TABS.filter(t => isRoleAllowed(t.roles));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.role]);

  return {
    admin, activeTab, setActiveTab, filter, setFilter,
    accessRequests, loading, actionLoading,
    usersRefreshKey, selectedRequest, setSelectedRequest,
    showAddAdvisorModal, setShowAddAdvisorModal,
    visibleTabs, visibleRoleTabs, isRoleAllowed,
    fetchUsers, handleApprove, handleWaitlist, handleDeleteUser, handleLogout,
    toast, setAdmin,
  };
}
